import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { List, ListItem, Popover, ListItemText } from '@material-ui/core';
import { DatePicker } from '~/modules/common/components';
import { useMeContext } from '~/modules/me';
import {
  getLuxonJsDateFormatFromMe,
  mapIsoStringtoUtcObject
} from '~/modules/common/dates/convert';
import { getUserPreferenceWeekStart } from '~/modules/common/dates/dayOfWeek';
import { PERIOD_SCALE_ENUM } from '~/modules/common/charts/timeline/periodScale';
import PeriodTitle from '~/modules/common/components/PeriodTitle';
import TodayPeriodValue from '~/modules/common/components/TodayPeriodValue';
import ChartDateRangeQuarterPicker from '../ChartDateRangePicker/ChartDateRangeQuarterPicker';
import { getPickerProps } from '../datePickerUtils';
import useDatePickerStyles from '../datePickerStyles';

const DEFAULT_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'left' };
const DEFAULT_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'left' };

export const PeriodStartDatePicker = ({
  menuId,
  anchorEl,
  scale,
  dateRange,
  onClose,
  onChange
}) => {
  const isOpen = Boolean(anchorEl);
  const me = useMeContext();
  const classes = useDatePickerStyles();

  const luxonFormat = getLuxonJsDateFormatFromMe(me);

  const handleValueChange = useCallback(
    value => {
      if (onChange) {
        onChange(value);
      }
      if (onClose) onClose();
    },
    [onChange, onClose]
  );

  const handleDateChange = useCallback(value => handleValueChange(value), [
    handleValueChange
  ]);

  const handleTodayClick = useCallback(() => {
    const today = DateTime.local();
    const startDate =
      scale === PERIOD_SCALE_ENUM.WEEKS
        ? getUserPreferenceWeekStart(me, today)
        : today;

    handleValueChange(startDate);
  }, [handleValueChange, scale, me]);

  const handleQuarterlyClick = useCallback(
    value => handleValueChange(value.startDate),
    [handleValueChange]
  );

  const pickerProps = getPickerProps(me, scale, classes);

  return scale === PERIOD_SCALE_ENUM.QUARTERS ? (
    <ChartDateRangeQuarterPicker
      dateRange={dateRange}
      menuId={menuId}
      anchorEl={anchorEl}
      onChange={handleQuarterlyClick}
      onClose={onClose}
      isRmp2025Q3Enhancements
    />
  ) : (
    <Popover
      id={menuId}
      anchorEl={anchorEl}
      anchorOrigin={DEFAULT_ANCHOR_ORIGIN}
      transformOrigin={DEFAULT_TRANSFORM_ORIGIN}
      open={isOpen}
      onClose={onClose}
    >
      <DatePicker
        {...pickerProps}
        autoOk
        variant="static"
        format={luxonFormat}
        value={mapIsoStringtoUtcObject(dateRange.startDate.toISODate())}
        onChange={handleDateChange}
      />
      <List disablePadding className={classes.quickLinksList}>
        <ListItem button onClick={handleTodayClick} data-testid="today-button">
          <ListItemText
            primary={<PeriodTitle scale={scale} />}
            secondary={<TodayPeriodValue scale={scale} />}
          />
        </ListItem>
      </List>
    </Popover>
  );
};

PeriodStartDatePicker.propTypes = {
  menuId: PropTypes.string.isRequired,
  scale: PropTypes.oneOf([
    PERIOD_SCALE_ENUM.DAYS,
    PERIOD_SCALE_ENUM.MONTHS,
    PERIOD_SCALE_ENUM.WEEKS,
    PERIOD_SCALE_ENUM.QUARTERS,
    PERIOD_SCALE_ENUM.YEARS
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
  anchorEl: PropTypes.object,
  dateRange: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default PeriodStartDatePicker;
